import React from "react";

import { getSipCredentials } from "~/services/sipService";
import { resolveSipRegistrationDomain, resolveSipWssUrl } from "~/services/calling/config";
import { SipPhone } from "~/services/calling/sipPhone";
import { setActiveSipPhone } from "~/services/calling/sipTeardown";
import {
  isConnectAbortedError,
  isRegisterInProgressError,
} from "~/services/calling/sipErrors";
import type {
  AgentPresence,
  CallIncomingPayload,
  PendingSupervise,
  SipCredentials,
  SipCallState,
  SipRegistrationStatus,
} from "~/services/calling/types";
import type { CallState } from "./callState";
import { applySipCallEnded } from "./callState";
import { applyAlarmSignal, applyIncomingCall, removeIncomingCallsByCorrelators } from "./callState";
import { INVITED_SUPERVISE_AUTO_ACCEPT_WINDOW_MS } from "./superviseAutoAccept";
import type { ConnectionHealthRecorder } from "~/context/call/connectionHealth/types";
import {
  normalizeSocketIdentityValue,
  parseSipUser,
  readUserSipExtension,
  buildSipIncomingFallback,
  isAlarmIncomingPayload,
  normalizeAlarmSignal,
  normalizeIncomingCall,
} from "~/context/call/helpers";

export type SipLifecycleIdentity = {
  extension: string | null;
  sipUsername: string | null;
};

export type UseSipLifecycleParams = {
  ownsSip: boolean;
  sipRef: React.RefObject<SipPhone | null>;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  sipIdentityRef: React.MutableRefObject<SipLifecycleIdentity>;
  sipSessionGenerationRef: React.MutableRefObject<number>;
  pendingSuperviseRef: React.MutableRefObject<PendingSupervise | null>;
  updateState: (next: Partial<CallState>) => void;
  setState: React.Dispatch<React.SetStateAction<CallState>>;
  user: {
    id?: string;
    name?: string | null;
    surname?: string | null;
    email?: string | null;
    extensionId?: string | null;
  } | null;
  onSipIdentityChange?: () => void;
  onSipSessionReady?: () => void;
  connectionHealthRecorderRef?: React.MutableRefObject<ConnectionHealthRecorder>;
};

export type UseSipLifecycleResult = {
  initializeSip: () => Promise<void>;
  trySuperviseAutoAccept: () => boolean;
};

const SUPERVISE_INVITE_WAIT_MS = INVITED_SUPERVISE_AUTO_ACCEPT_WINDOW_MS;

export function useSipLifecycle({
  ownsSip,
  sipRef,
  audioRef,
  sipIdentityRef,
  sipSessionGenerationRef,
  pendingSuperviseRef,
  updateState,
  setState,
  user,
  onSipIdentityChange,
  onSipSessionReady,
  connectionHealthRecorderRef,
}: UseSipLifecycleParams): UseSipLifecycleResult {
  const activeDeviceCallIds = React.useRef<Set<string>>(new Set());
  const superviseAcceptInFlightRef = React.useRef(false);
  const ownsSipRef = React.useRef(ownsSip);

  ownsSipRef.current = ownsSip;

  const clearPendingSupervise = React.useCallback(() => {
    pendingSuperviseRef.current = null;
  }, [pendingSuperviseRef]);

  const peekPendingSuperviseMode = React.useCallback(() => {
    return pendingSuperviseRef.current?.mode ?? null;
  }, [pendingSuperviseRef]);
  const userSipExtension = readUserSipExtension(user);
  const userExtensionId = user?.extensionId;
  const userName = user?.name;
  const userSurname = user?.surname;
  const userEmail = user?.email;

  const currentAgent = React.useMemo((): AgentPresence | null => {
    if (!user?.id) return null;
    const name = [user.name, user.surname].filter(Boolean).join(" ").trim();
    return {
      id: user.id,
      name: (name || user.email) ?? user.id,
      status: "online",
      sipExtension: user.extensionId ?? null,
      sipUri: null,
      profileImageUrl: (user as { profilePicUrl?: string }).profilePicUrl ?? null,
    };
  }, [user]);

  const trySuperviseAutoAccept = React.useCallback(() => {
    if (superviseAcceptInFlightRef.current) return true;

    const superviseMode = peekPendingSuperviseMode();
    if (!superviseMode) return false;

    const sipPhone = sipRef.current;
    if (!sipPhone) return false;

    superviseAcceptInFlightRef.current = true;

    console.log("[SipLifecycle] Supervise auto-accept: answering INVITE", {
      mode: superviseMode,
    });

    const waitForPendingInvitation = async () => {
      const deadline = Date.now() + SUPERVISE_INVITE_WAIT_MS;
      while (!sipPhone.hasIncomingInvitation() && Date.now() < deadline) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 100));
      }
      if (!sipPhone.hasIncomingInvitation()) {
        throw new Error("Supervise INVITE not received in time.");
      }
    };

    void waitForPendingInvitation()
      .then(() => sipPhone.answer({ superviseMode }))
      .then(() => {
        clearPendingSupervise();
        updateState({
          callStatus: "active",
          callDirection: "incoming",
          callStartedAt: Date.now(),
          activeSuperviseMode: superviseMode,
        });
      })
      .catch((error) => {
        console.error("[SipLifecycle] Supervise auto-accept failed", error);
        const inviteNotReady =
          error instanceof Error &&
          error.message.includes("Supervise INVITE not received in time.");
        if (!inviteNotReady) {
          clearPendingSupervise();
          updateState({ activeSuperviseMode: null });
        }
      })
      .finally(() => {
        superviseAcceptInFlightRef.current = false;
      });

    return true;
  }, [clearPendingSupervise, peekPendingSuperviseMode, sipRef, updateState]);

  const handleSipStatus = React.useCallback(
    (status: SipRegistrationStatus) => {
      updateState({ sipStatus: status });
    },
    [updateState]
  );

  const handleSipCallState = React.useCallback(
    (next: SipCallState, _session: unknown, direction?: "incoming" | "outgoing") => {
      setState((prev) => {
        const updated: CallState = {
          ...prev,
          callStatus: next,
          callDirection: direction ?? prev.callDirection,
        };
        if (next === "active") {
          updated.callStartedAt = updated.callStartedAt ?? Date.now();
          if (direction === "outgoing" && prev.backgroundHeldCallUuid) {
            updated.isOnHold = false;
            updated.consultStatus = "active";
          } else if (direction !== "outgoing") {
            updated.isOnHold = false;
          }
          if (currentAgent) {
            const exists = prev.participants.some(
              (agent) => agent.id === currentAgent.id
            );
            updated.participants = exists
              ? prev.participants
              : [currentAgent, ...prev.participants];
          }
        }
        if (next === "held") {
          updated.isOnHold = true;
          updated.callDirection = "incoming";
          if (prev.backgroundHeldCallUuid) {
            updated.callUuid = prev.backgroundHeldCallUuid;
            updated.callReference =
              prev.backgroundHeldCallReference ?? prev.callReference ?? null;
            updated.callerNumber =
              prev.heldCallerNumber ?? prev.callerNumber ?? null;
          }
        }
        if (next === "ended") {
          return applySipCallEnded(updated);
        }
        return updated;
      });
    },
    [currentAgent, setState]
  );

  const handleSipIncoming = React.useCallback((fromUri: string | null) => {
    if (trySuperviseAutoAccept()) return;

    setState((prev) => {
      const callerFromSip = parseSipUser(fromUri);
      const existingMatch = prev.incomingCalls.find((call) => {
        if (!callerFromSip || !call.callerNumber) return false;
        return call.callerNumber === callerFromSip;
      });
      const fallbackIncoming = existingMatch
        ? { ...existingMatch, receivedAtMs: Date.now() }
        : buildSipIncomingFallback(fromUri);
      return applyIncomingCall(prev, fallbackIncoming);
    });
  }, [setState, trySuperviseAutoAccept]);

  const handleSipIncomingPayload = React.useCallback(
    (payload: CallIncomingPayload, source?: string) => {
      console.log("[IncomingCall][SIP]", {
        source: source ?? null,
        id: payload.id ?? null,
        callUuid: payload.callUuid ?? null,
        callId: payload.callId ?? null,
        callerNumber: payload.callerNumber ?? null,
        customerName: payload.customerName ?? null,
        service: payload.service ?? null,
        signalType: payload.signalType ?? null,
        description: payload.description ?? null,
        incomingMessage: payload.description ?? payload.metadata ?? null,
        receivedAtMs: payload.receivedAtMs ?? null,
        metadata: payload.metadata ?? null,
        rawPayload: payload,
      });

      if (trySuperviseAutoAccept()) return;

      if (payload.callerNumber === "200" && !payload.service) {
        console.log(
          "[SipLifecycle] Ignoring FreePBX UCP event (monitoring only)"
        );
        return;
      }
      const payloadCallId = payload.callUuid ?? payload.callId ?? payload.id;
      if (payloadCallId) {
        if (activeDeviceCallIds.current.has(payloadCallId)) {
          console.log(
            `[SipLifecycle] Ignoring duplicate callId=${payloadCallId}`
          );
          return;
        }
        activeDeviceCallIds.current.add(payloadCallId);
        if (activeDeviceCallIds.current.size > 1000) {
          activeDeviceCallIds.current.clear();
          activeDeviceCallIds.current.add(payloadCallId);
        }
      }
      setState((prev) => {
        if (isAlarmIncomingPayload(payload)) {
          return applyAlarmSignal(prev, normalizeAlarmSignal(payload));
        }
        return applyIncomingCall(prev, normalizeIncomingCall(payload));
      });
    },
    [setState, trySuperviseAutoAccept]
  );

  const handleSipError = React.useCallback(
    (error: Error) => {
      if (isRegisterInProgressError(error) || isConnectAbortedError(error)) {
        return;
      }

      console.error("SIP error", {
        message: error.message,
        sipWssUrl: resolveSipWssUrl(),
        sipDomain: resolveSipRegistrationDomain(resolveSipWssUrl()),
      });
      updateState({ lastError: error.message, sipStatus: "error" });
    },
    [updateState]
  );

  const handleSipInvitationEnded = React.useCallback(
    (correlatorIds: string[]) => {
      setState((prev) => removeIncomingCallsByCorrelators(prev, correlatorIds));
    },
    [setState]
  );

  const handleSipBackgroundHeldEnded = React.useCallback(() => {
    updateState({
      backgroundHeldCallUuid: null,
      backgroundHeldCallReference: null,
      heldCallerNumber: null,
      lastError: "Alarm caller hung up while you were on a consult call.",
    });
  }, [updateState]);

  const handleSipSessionsSwapped = React.useCallback(
    (payload: {
      foregroundDirection: "incoming" | "outgoing";
      backgroundDirection: "incoming" | "outgoing";
    }) => {
      setState((prev) => ({
        ...prev,
        callDirection: payload.foregroundDirection,
        callStatus: "active",
        isOnHold: false,
      }));
    },
    [setState]
  );

  const initializeSip = React.useCallback(async () => {
    const initGeneration = sipSessionGenerationRef.current;
    const existingPhone = sipRef.current;
    if (existingPhone && ownsSip) {
      const transportState = existingPhone.getTransportState();
      const registrationState = existingPhone.registrationState;
      if (
        transportState === "Connected" &&
        (registrationState === "connecting" || registrationState === "registered")
      ) {
        if (registrationState === "connecting") {
          void existingPhone.register().catch((error) => handleSipError(error as Error));
        }
        return;
      }

      if (
        transportState === "Connecting" &&
        registrationState === "connecting"
      ) {
        return;
      }
    }

    const sipWssUrl = resolveSipWssUrl();
    if (!sipWssUrl) {
      updateState({ lastError: "Missing SIP WSS URL.", sipStatus: "error" });
      return;
    }

    const sipDomain = resolveSipRegistrationDomain(sipWssUrl);
    if (!sipDomain) {
      updateState({ lastError: "Missing SIP domain.", sipStatus: "error" });
      return;
    }

    updateState({ sipDomain });

    try {
      const raw = await getSipCredentials();
      if (initGeneration !== sipSessionGenerationRef.current) {
        return;
      }
      const extension =
        normalizeSocketIdentityValue(raw?.extension) ??
        normalizeSocketIdentityValue(userSipExtension) ??
        normalizeSocketIdentityValue(userExtensionId);
      const username =
        extension ??
        (typeof raw?.username === "string" ? raw.username : null) ??
        userExtensionId ??
        "";
      const password =
        typeof raw?.password === "string" ? raw.password : "";
      const authUsername =
        extension ??
        (typeof raw?.authUsername === "string" ? raw.authUsername : undefined) ??
        (typeof raw?.username === "string" ? raw.username : undefined);
      const displayName =
        typeof raw?.displayName === "string"
          ? raw.displayName
          : [userName, userSurname].filter(Boolean).join(" ") || userEmail || undefined;
      const domain =
        typeof raw?.domain === "string" ? raw.domain : sipDomain;

      if (!username || !password) {
        throw new Error("Missing SIP credentials.");
      }

      const nextSipUsername = normalizeSocketIdentityValue(username);
      const nextExtension =
        extension ??
        normalizeSocketIdentityValue(userSipExtension) ??
        normalizeSocketIdentityValue(userExtensionId) ??
        nextSipUsername;
      const identityChanged =
        sipIdentityRef.current.extension !== nextExtension ||
        sipIdentityRef.current.sipUsername !== nextSipUsername;
      if (identityChanged) {
        sipIdentityRef.current = {
          extension: nextExtension,
          sipUsername: nextSipUsername,
        };
        onSipIdentityChange?.();
      }

      if (!ownsSipRef.current) {
        updateState({ sipStatus: "offline" });
        return;
      }

      if (!sipRef.current) {
        sipRef.current = new SipPhone({
          onRegistrationStatus: handleSipStatus,
          onTransportStateChange: (transportState) => {
            if (transportState === "connected") {
              connectionHealthRecorderRef?.current.recordSipTransportConnect();
              return;
            }
            connectionHealthRecorderRef?.current.recordSipTransportDisconnect();
          },
          onCallStateChange: handleSipCallState,
          onIncomingCall: handleSipIncoming,
          onIncomingCallPayload: handleSipIncomingPayload,
          onInvitationEnded: handleSipInvitationEnded,
          onBackgroundHeldEnded: handleSipBackgroundHeldEnded,
          onSessionsSwapped: handleSipSessionsSwapped,
          onError: handleSipError,
        });
      }
      setActiveSipPhone(sipRef.current);
      sipRef.current.setAudioElement(audioRef.current);

      const credentials: SipCredentials = {
        username,
        authUsername: authUsername ?? username,
        password,
        displayName,
        domain: domain ?? sipDomain,
      };

      const resolvedDomain = credentials.domain ?? sipDomain;
      updateState({ sipDomain: resolvedDomain });
      if (!ownsSipRef.current) {
        updateState({ sipStatus: "offline" });
        return;
      }

      await sipRef.current.connectAndRegister({
        sipWssUrl,
        sipDomain: resolvedDomain,
        credentials,
      });
      if (
        !ownsSipRef.current ||
        initGeneration !== sipSessionGenerationRef.current
      ) {
        await sipRef.current.stop();
      }
    } catch (error) {
      handleSipError(error as Error);
    } finally {
      onSipSessionReady?.();
    }
  }, [
    audioRef,
    handleSipCallState,
    handleSipError,
    handleSipIncoming,
    handleSipIncomingPayload,
    handleSipInvitationEnded,
    handleSipStatus,
    onSipIdentityChange,
    onSipSessionReady,
    ownsSip,
    ownsSipRef,
    sipIdentityRef,
    sipRef,
    sipSessionGenerationRef,
    updateState,
    userEmail,
    userExtensionId,
    userName,
    userSipExtension,
    userSurname,
  ]);

  React.useEffect(() => {
    const onFocus = () => {
      if (!ownsSipRef.current) return;

      const sipPhone = sipRef.current;
      if (!sipPhone) return;
      if (!sipPhone.canAttemptRegister()) return;

      void sipPhone.register().catch((error) => handleSipError(error as Error));
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [handleSipError, ownsSipRef, sipRef]);

  return { initializeSip, trySuperviseAutoAccept };
}
